/* eslint-disable no-restricted-globals */
// CalendarScreen.jsx

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import {
  Box,
  Typography,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import CalendarScreenStyles from "./CalendarScreen.styles";
import AvailabilitySetter from "./AvailabilitySetter";
import useRealtimeAvailability from "../../hooks/useRealtimeAvailability";

const localizer = momentLocalizer(moment);

/* ---------- utils ---------- */
const roundToQuarter = (date) =>
  moment(date)
    .minutes(Math.floor(moment(date).minutes() / 15) * 15)
    .seconds(0)
    .milliseconds(0);

const dayAbbr = {
  Monday: "MO",
  Tuesday: "TU",
  Wednesday: "WE",
  Thursday: "TH",
  Friday: "FR",
  Saturday: "SA",
  Sunday: "SU",
};

// tiny transparent drag image to prevent snap-back ghost
const getTransparentDragImage = () => {
  const c = document.createElement("canvas");
  c.width = 1; c.height = 1;
  return c;
};

/**
 * Make event DOM elements draggable and attach the event id to HTML5 dataTransfer.
 * Only allow dragging for persisted events (not preview/__pending).
 * Also handles single-click-to-select (without opening modal).
 */
const DraggableEventWrapper = ({ children, event, onDragState, onSelect }) => {
  const draggable = !!event?.id && !event?.__pending; // only real, saved slots
  const idStr = String(event?.id ?? "");

  return React.cloneElement(React.Children.only(children), {
    draggable,
    onClick: (e) => {
      // Single click only selects/toggles (no modal here)
      e.stopPropagation();
      if (!idStr) return;
      onSelect?.(idStr, e);
    },
    onDragStart: (e) => {
      if (!draggable) return;
      try {
        onDragState?.("start", idStr);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", idStr);
        e.dataTransfer.setData("application/x-calendar-event-id", idStr);
        const img = getTransparentDragImage();
        e.dataTransfer.setDragImage(img, 0, 0);
        e.stopPropagation();
      } catch {
        /* noop */
      }
    },
    onDragEnd: () => {
      onDragState?.("end", idStr);
    },
  });
};

const CalendarScreen = () => {
  const navigate = useNavigate();

  const {
    getAvailabilityForAllProperties,
    fetchUserProperties,
    deleteAvailability,
  } = useRealtimeAvailability();

  /* ---------- state ---------- */
  const [availabilities, setAvailabilities] = useState([]);
  const [events, setEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState("all");

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date()); 
  const [pendingPreview, setPendingPreview] = useState([]);   
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set()); 
  const [targetDate, setTargetDate] = useState(null); 
  const [draggingId, setDraggingId] = useState(null);
  const draggingRef = useRef(false);

  // Dropdown picker selections
  const [pickedSlotIds, setPickedSlotIds] = useState([]);

  /* ---------- data fetch ---------- */
  const refreshAvail = useCallback(async () => {
    const av = await getAvailabilityForAllProperties();
    setAvailabilities(av ?? []);
  }, [getAvailabilityForAllProperties]);

  useEffect(() => {
    refreshAvail();
    (async () => {
      const list = await fetchUserProperties();
      setProperties(list);
    })();
  }, [refreshAvail, fetchUserProperties]);

  /* ---------- build & filter events ---------- */
  useEffect(() => {
    const allEvents = [];

    availabilities.forEach((a) => {
      if (!a.startTime || !a.endTime) return;

      if (a.date) {
        allEvents.push({
          ...a,
          id: String(a.id),
          title: a.isMaintenance ? "Maintenance" : "Available",
          start: moment(a.startTime).toDate(),
          end: moment(a.endTime).toDate(),
          recurring: false,
        });
      } else if (a.day) {
        const dayIdx = moment().day(a.day).day();
        const st = moment(a.startTime);
        const et = moment(a.endTime);
        let recent = moment().day(dayIdx);
        if (recent.isAfter(moment())) recent.subtract(7, "days");

        allEvents.push({
          ...a,
          id: String(a.id),
          title: a.isMaintenance ? "Maintenance" : "Available",
          start: recent.clone().set({ hour: st.hour(), minute: st.minute() }).toDate(),
          end: recent.clone().set({ hour: et.hour(), minute: et.minute() }).toDate(),
          rrule: `FREQ=WEEKLY;BYDAY=${dayAbbr[a.day]}`,
          recurring: true,
        });
      }
    });

    const filteredPersisted =
      propertyFilter === "all"
        ? allEvents
        : allEvents.filter((e) => e.propertyId === propertyFilter);

    const filteredPreview =
      propertyFilter === "all"
        ? pendingPreview
        : pendingPreview.filter((e) => e.propertyId === propertyFilter);

    const previewEvents = filteredPreview.map((p) => ({ ...p, __pending: true, id: String(p.id) }));
    setEvents([...filteredPersisted, ...previewEvents]);
  }, [availabilities, propertyFilter, pendingPreview]);

  /* ---------- selection helpers ---------- */
  const toggleSelectId = (id) => {
    const idStr = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(idStr) ? next.delete(idStr) : next.add(idStr);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const getIdsForDay = (dateLike) => {
    const start = moment(dateLike).startOf("day");
    const end = moment(dateLike).endOf("day");
    return events
      .filter((e) => !e.__pending)
      .filter((e) => moment(e.start).isSameOrAfter(start) && moment(e.end).isSameOrBefore(end))
      .map((e) => String(e.id));
  };

  /* ---------- handlers ---------- */
  // Single-click selection (no modal)
  const handleEventClickSelect = (idStr, nativeEvent) => {
    const multiKey = nativeEvent?.metaKey || nativeEvent?.ctrlKey || selectMode;
    if (multiKey) {
      toggleSelectId(idStr);
      return;
    }
    // If not in select mode, single click selects the item (and de-selects others)
    setSelectedIds(new Set([idStr]));
  };

  // Double click opens details (reduces accidental popups)
  const handleDoubleClickEvent = (e) => {
    if (draggingRef.current) return; // ignore if just dragged
    setSelectedEvent(e);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    await deleteAvailability(selectedEvent.propertyId, String(selectedEvent.id));
    setAvailabilities((prev) => prev.filter((a) => String(a.id) !== String(selectedEvent.id)));
    setModalOpen(false);
  };

  const availabilitySetterRef = useRef();

  const handleSlotSelect = ({ start }) => {
    // Mark target day (visual + clear-day/week anchor)
    setTargetDate(moment(start).toDate());

    if (selectMode) {
      // In select mode: selecting a day selects ALL its slots so Delete Selected can clear the day.
      const ids = getIdsForDay(start);
      setSelectedIds(new Set(ids));
      return;
    }

    const s = roundToQuarter(start);
    const e = moment(s).add(15, "minutes").toDate();

    availabilitySetterRef.current.open({
      selectedDates: [s.toDate()],
      startTime: s.toDate(),
      endTime: e,
      defaultPropertyId: propertyFilter !== "all" ? propertyFilter : "",
    });
  };

  // ---------- Bulk clear (sequential per property to avoid Firebase overwrite races) ----------
  const deleteIds = async (ids) => {
    const list = (Array.isArray(ids) ? ids : [...ids]).map((x) => String(x));
    if (!list.length) return;

    // Group by property to keep sequential within each property (avoids set() race)
    const byProperty = new Map();
    for (const id of list) {
      const a = availabilities.find((x) => String(x.id) === id);
      if (!a) continue;
      if (!byProperty.has(a.propertyId)) byProperty.set(a.propertyId, []);
      byProperty.get(a.propertyId).push(id);
    }

    for (const [propertyId, idList] of byProperty.entries()) {
      for (const id of idList) {
        await deleteAvailability(propertyId, id); // sequential
      }
    }

    setAvailabilities((prev) => prev.filter((a) => !list.includes(String(a.id))));
    clearSelection();
  };

  const deleteSelected = () => {
    const list = [...selectedIds]; // already strings
    if (!list.length) return;
    if (confirm(`Delete ${list.length} selected slot(s)?`)) {
      deleteIds(list);
    }
  };

  const effectiveDay = targetDate || currentDate;

  const clearDay = () => {
    const ids = getIdsForDay(effectiveDay);
    if (!ids.length) return;
    if (confirm(`Delete ${ids.length} slot(s) on ${moment(effectiveDay).startOf("day").format("LL")}?`)) {
      deleteIds(ids);
    }
  };

  const clearWeek = () => {
    const start = moment(effectiveDay).startOf("week");
    const end = moment(effectiveDay).endOf("week");
    const ids = events
      .filter((e) => !e.__pending)
      .filter((e) => moment(e.start).isSameOrAfter(start) && moment(e.end).isSameOrBefore(end))
      .map((e) => String(e.id));
    if (!ids.length) return;
    if (confirm(`Delete ${ids.length} slot(s) for ${start.format("ll")} – ${end.format("ll")}?`)) {
      deleteIds(ids);
    }
  };

  const clearAllFiltered = () => {
    const ids = events.filter((e) => !e.__pending).map((e) => String(e.id));
    if (!ids.length) return;
    if (confirm(`Delete ALL ${ids.length} slot(s) currently shown?`)) {
      deleteIds(ids);
    }
  };

  // ---------- Dropdown helpers ----------
  // Persisted (non-preview) events currently shown by the property filter
  const shownSlots = events
    .filter((e) => !e.__pending)
    .sort((a, b) => +a.start - +b.start);

  const labelFor = (slot) => {
    const when = `${moment(slot.start).format("ddd, MMM D, HH:mm")}–${moment(slot.end).format("HH:mm")}`;
    const kind = slot.isMaintenance ? "Maintenance" : "Available";
    const prop = properties.find((p) => p.id === slot.propertyId);
    const propName = prop?.name || prop?.address || `Property ${slot.propertyId ?? ""}`;
    return `${when} • ${kind} • ${propName}`;
  };

  const handlePickedChange = (e) => {
    const value = e.target.value;
    setPickedSlotIds(typeof value === "string" ? value.split(",") : value);
  };

  const deletePicked = () => {
    if (!pickedSlotIds.length) return;
    if (confirm(`Delete ${pickedSlotIds.length} picked slot(s)?`)) {
      deleteIds(pickedSlotIds);
      setPickedSlotIds([]);
    }
  };

  const clearAllEverywhere = () => {
    const allIds = availabilities.map((a) => String(a.id));
    if (!allIds.length) return;
    if (confirm(`Delete ALL ${allIds.length} slot(s) across ALL properties?`)) {
      deleteIds(allIds);
      setPickedSlotIds([]);
    }
  };

  // Keyboard delete
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.size) {
          e.preventDefault();
          deleteSelected();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // helper passed into event wrapper
  const handleDragState = (phase, id) => {
    if (phase === "start") {
      draggingRef.current = true;
      setDraggingId(id);
    } else {
      setTimeout(() => { draggingRef.current = false; }, 0);
      setDraggingId(null);
    }
  };

  /* ---------- render ---------- */
  return (
    <Box sx={CalendarScreenStyles.container}>
      {/* back to dashboard */}
      <IconButton
        onClick={() => navigate(-1)}          /* or navigate("/dashboard") */
        sx={{ position: "absolute", top: 16, left: 16 }}
        aria-label="Back"
      >
        <ArrowBackIosNewIcon />
      </IconButton>

      <Typography variant="h4" sx={CalendarScreenStyles.title} mt={1}>
        Viewing &amp; Maintenance Schedule
      </Typography>

      {/* property selector + bulk actions */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Show slots for…</InputLabel>
          <Select
            value={propertyFilter}
            label="Show slots for…"
            onChange={(e) => setPropertyFilter(e.target.value)}
          >
            <MenuItem value="all">All Properties</MenuItem>
            {properties.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name || p.address || `Property ${p.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Single-click selects • Double-click opens details • Delete key removes selection">
          <FormControlLabel
            control={<Switch checked={selectMode} onChange={(e) => setSelectMode(e.target.checked)} />}
            label="Select Mode"
          />
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          onClick={deleteSelected}
          disabled={!selectedIds.size}
        >
          Delete Selected ({selectedIds.size})
        </Button>
        <Button size="small" variant="outlined" onClick={clearDay}>
          Clear Day
        </Button>
        <Button size="small" variant="outlined" onClick={clearWeek}>
          Clear Week
        </Button>
        <Button size="small" color="error" variant="outlined" onClick={clearAllFiltered}>
          Clear All (Filtered)
        </Button>

        {/* --- Availability Picker (multi-select dropdown) --- */}
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel id="slots-picker-label">Browse slots…</InputLabel>
          <Select
            labelId="slots-picker-label"
            multiple
            value={pickedSlotIds}
            onChange={handlePickedChange}
            label="Browse slots…"
            renderValue={(selected) =>
              selected.length ? `${selected.length} selected` : "None"
            }
            MenuProps={{ PaperProps: { style: { maxHeight: 420, width: 420 } } }}
          >
            {shownSlots.length === 0 && (
              <MenuItem disabled>(No slots in current view/filter)</MenuItem>
            )}
            {shownSlots.map((slot) => {
              const id = String(slot.id);
              const checked = pickedSlotIds.indexOf(id) > -1;
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox checked={checked} />
                  <ListItemText
                    primary={labelFor(slot)}
                    secondary={
                      slot.recurring && slot.rrule
                        ? `Recurring • ${slot.rrule}`
                        : undefined
                    }
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={deletePicked}
          disabled={!pickedSlotIds.length}
        >
          Delete Picked ({pickedSlotIds.length})
        </Button>

        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={clearAllEverywhere}
        >
          Delete ALL (All Properties)
        </Button>

        {/* Visual indicator of clicked day */}
        {targetDate && (
          <Chip
            size="small"
            label={`Target day: ${moment(targetDate).format("ll")}`}
            sx={{ ml: 1 }}
          />
        )}

        {!!pendingPreview.length && (
          <Typography variant="caption">
            Previewing {pendingPreview.length} unsaved slot(s)
          </Typography>
        )}
      </Box>

      <AvailabilitySetter
        ref={availabilitySetterRef}
        onAddAvailability={(newAv) => setAvailabilities((p) => [...p, ...newAv])}
        onPreviewChange={setPendingPreview}
        propertiesSeed={properties}
      />

      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        step={15}
        timeslots={4}
        selectable
        min={moment().startOf("day").add(6, "h").toDate()}
        max={moment().startOf("day").add(22, "h").toDate()}
        defaultView={view}
        defaultDate={currentDate}
        onView={(v) => {
          setView(v);
          setTargetDate((d) => d);
        }}
        onNavigate={(date) => setCurrentDate(date)}
        onSelecting={() => !selectMode}
        onSelectSlot={(slot) => {
          // Always delegate to handleSlotSelect; it handles selectMode vs normal.
          handleSlotSelect(slot);
        }}
        // IMPORTANT: we no longer use RBC's onSelectEvent to avoid accidental popups
        onDoubleClickEvent={handleDoubleClickEvent}
        style={CalendarScreenStyles.calendar}
        components={{
          eventWrapper: (props) => (
            <DraggableEventWrapper
              {...props}
              onDragState={handleDragState}
              onSelect={handleEventClickSelect}
            />
          ),
        }}
        eventPropGetter={(e) => {
          const base = e.isMaintenance ? "#333333" : "#81c241";
          const isSelected = selectedIds.has(String(e.id));
          const isDragging = draggingId === String(e.id);
          const style = {
            backgroundColor: base,
            opacity: e.__pending ? 0.5 : (isDragging ? 0.75 : 1),
            border: e.__pending
              ? "2px dashed #666666"
              : (isSelected ? "2px solid #000000" : (isDragging ? "2px solid #81c241" : "1px solid rgba(0,0,0,0.15)")),
            boxShadow: isSelected
              ? "0 0 0 3px rgba(0,0,0,0.15) inset"
              : (isDragging ? "0 4px 12px rgba(0,0,0,0.24)" : undefined),
            transform: isDragging ? "scale(0.98)" : undefined,
            cursor: (!e.__pending && e.id) ? (isDragging ? "grabbing" : "grab") : "pointer",
            transition: "transform 80ms ease, opacity 80ms ease, border 80ms ease",
          };
          return { style };
        }}
        dayPropGetter={(date) => {
          const isTarget = targetDate && moment(date).isSame(targetDate, "day");
          if (!isTarget) return {};
          return {
            style: {
              backgroundColor: "rgba(129,194,65,0.08)",
              outline: "2px solid #81c241",
              outlineOffset: "-2px",
            },
          };
        }}
        longPressThreshold={280} // friendlier touch behavior
      />

      {/* modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={CalendarScreenStyles.modalContent}>
          <Typography variant="h6">Event Details</Typography>
          <Typography>Title: {selectedEvent?.title}</Typography>
          <Typography>
            Start:&nbsp;
            {selectedEvent?.start && moment(selectedEvent.start).format("LLL")}
          </Typography>
          <Typography>
            End:&nbsp;
            {selectedEvent?.end && moment(selectedEvent.end).format("LLL")}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button variant="outlined" onClick={handleDelete}>
              Delete This
            </Button>
            <Button variant="contained" color="secondary" onClick={() => setModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CalendarScreen;












/* eslint-disable no-restricted-globals */
// CalendarScreen.jsx

// import React, {
//   useState,
//   useEffect,
//   useCallback,
//   useRef,
// } from "react";
// import { useNavigate } from "react-router-dom";
// import moment from "moment";
// import { Calendar, momentLocalizer } from "react-big-calendar";
// import "react-big-calendar/lib/css/react-big-calendar.css";

// import {
//   Box,
//   Typography,
//   Modal,
//   Button,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   IconButton,
//   Switch,
//   FormControlLabel,
//   Tooltip,
//   Chip,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// import CalendarScreenStyles from "./CalendarScreen.styles";
// import AvailabilitySetter from "./AvailabilitySetter";
// import useRealtimeAvailability from "../../hooks/useRealtimeAvailability";

// const localizer = momentLocalizer(moment);

// /* ---------- utils ---------- */
// const roundToQuarter = (date) =>
//   moment(date)
//     .minutes(Math.floor(moment(date).minutes() / 15) * 15)
//     .seconds(0)
//     .milliseconds(0);

// const dayAbbr = {
//   Monday: "MO",
//   Tuesday: "TU",
//   Wednesday: "WE",
//   Thursday: "TH",
//   Friday: "FR",
//   Saturday: "SA",
//   Sunday: "SU",
// };

// // tiny transparent drag image to prevent snap-back ghost
// const getTransparentDragImage = () => {
//   const c = document.createElement("canvas");
//   c.width = 1; c.height = 1;
//   return c;
// };

// /**
//  * Make event DOM elements draggable and attach the event id to HTML5 dataTransfer.
//  * Only allow dragging for persisted events (not preview/__pending).
//  * Also handles single-click-to-select (without opening modal).
//  */
// const DraggableEventWrapper = ({ children, event, onDragState, onSelect }) => {
//   const draggable = !!event?.id && !event?.__pending; // only real, saved slots
//   const idStr = String(event?.id ?? "");

//   return React.cloneElement(React.Children.only(children), {
//     draggable,
//     onClick: (e) => {
//       // Single click only selects/toggles (no modal here)
//       e.stopPropagation();
//       if (!idStr) return;
//       onSelect?.(idStr, e);
//     },
//     onDragStart: (e) => {
//       if (!draggable) return;
//       try {
//         onDragState?.("start", idStr);
//         e.dataTransfer.effectAllowed = "move";
//         e.dataTransfer.setData("text/plain", idStr);
//         e.dataTransfer.setData("application/x-calendar-event-id", idStr);
//         const img = getTransparentDragImage();
//         e.dataTransfer.setDragImage(img, 0, 0);
//         e.stopPropagation();
//       } catch {
//         /* noop */
//       }
//     },
//     onDragEnd: () => {
//       onDragState?.("end", idStr);
//     },
//   });
// };

// const CalendarScreen = () => {
//   const navigate = useNavigate();

//   const {
//     getAvailabilityForAllProperties,
//     fetchUserProperties,
//     deleteAvailability,
//   } = useRealtimeAvailability();

//   /* ---------- state ---------- */
//   const [availabilities, setAvailabilities] = useState([]);
//   const [events, setEvents] = useState([]);
//   const [properties, setProperties] = useState([]);
//   const [propertyFilter, setPropertyFilter] = useState("all");

//   const [selectedEvent, setSelectedEvent] = useState(null);
//   const [modalOpen, setModalOpen] = useState(false);
//   const [view, setView] = useState("month");
//   const [currentDate, setCurrentDate] = useState(new Date()); // view anchor
//   const [pendingPreview, setPendingPreview] = useState([]);   // dashed, unsaved slots

//   // Selection & targeting
//   const [selectMode, setSelectMode] = useState(false);
//   const [selectedIds, setSelectedIds] = useState(new Set()); // store as strings
//   const [targetDate, setTargetDate] = useState(null); // last clicked day/slot start

//   // Drag state (prevents click modal + styles while dragging)
//   const [draggingId, setDraggingId] = useState(null);
//   const draggingRef = useRef(false);

//   // Trash visual
//   const [trashHot, setTrashHot] = useState(false);

//   /* ---------- data fetch ---------- */
//   const refreshAvail = useCallback(async () => {
//     const av = await getAvailabilityForAllProperties();
//     setAvailabilities(av ?? []);
//   }, [getAvailabilityForAllProperties]);

//   useEffect(() => {
//     refreshAvail();
//     (async () => {
//       const list = await fetchUserProperties();
//       setProperties(list);
//     })();
//   }, [refreshAvail, fetchUserProperties]);

//   /* ---------- build & filter events ---------- */
//   useEffect(() => {
//     const allEvents = [];

//     availabilities.forEach((a) => {
//       if (!a.startTime || !a.endTime) return;

//       if (a.date) {
//         allEvents.push({
//           ...a,
//           id: String(a.id),
//           title: a.isMaintenance ? "Maintenance" : "Available",
//           start: moment(a.startTime).toDate(),
//           end: moment(a.endTime).toDate(),
//           recurring: false,
//         });
//       } else if (a.day) {
//         const dayIdx = moment().day(a.day).day();
//         const st = moment(a.startTime);
//         const et = moment(a.endTime);
//         let recent = moment().day(dayIdx);
//         if (recent.isAfter(moment())) recent.subtract(7, "days");

//         allEvents.push({
//           ...a,
//           id: String(a.id),
//           title: a.isMaintenance ? "Maintenance" : "Available",
//           start: recent.clone().set({ hour: st.hour(), minute: st.minute() }).toDate(),
//           end: recent.clone().set({ hour: et.hour(), minute: et.minute() }).toDate(),
//           rrule: `FREQ=WEEKLY;BYDAY=${dayAbbr[a.day]}`,
//           recurring: true,
//         });
//       }
//     });

//     const filteredPersisted =
//       propertyFilter === "all"
//         ? allEvents
//         : allEvents.filter((e) => e.propertyId === propertyFilter);

//     const filteredPreview =
//       propertyFilter === "all"
//         ? pendingPreview
//         : pendingPreview.filter((e) => e.propertyId === propertyFilter);

//     const previewEvents = filteredPreview.map((p) => ({ ...p, __pending: true, id: String(p.id) }));
//     setEvents([...filteredPersisted, ...previewEvents]);
//   }, [availabilities, propertyFilter, pendingPreview]);

//   /* ---------- selection helpers ---------- */
//   const toggleSelectId = (id) => {
//     const idStr = String(id);
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       next.has(idStr) ? next.delete(idStr) : next.add(idStr);
//       return next;
//     });
//   };

//   const clearSelection = () => setSelectedIds(new Set());

//   /* ---------- handlers ---------- */
//   // Single-click selection (no modal)
//   const handleEventClickSelect = (idStr, nativeEvent) => {
//     const multiKey = nativeEvent?.metaKey || nativeEvent?.ctrlKey || selectMode;
//     if (multiKey) {
//       toggleSelectId(idStr);
//       return;
//     }
//     // If not in select mode, single click selects the item (and de-selects others)
//     setSelectedIds(new Set([idStr]));
//   };

//   // Double click opens details (reduces accidental popups)
//   const handleDoubleClickEvent = (e) => {
//     if (draggingRef.current) return; // ignore if just dragged
//     setSelectedEvent(e);
//     setModalOpen(true);
//   };

//   const handleDelete = async () => {
//     if (!selectedEvent) return;
//     await deleteAvailability(selectedEvent.propertyId, String(selectedEvent.id));
//     setAvailabilities((prev) => prev.filter((a) => String(a.id) !== String(selectedEvent.id)));
//     setModalOpen(false);
//   };

//   const availabilitySetterRef = useRef();

//   const handleSlotSelect = ({ start }) => {
//     // Mark target day (visual + clear-day/week anchor)
//     setTargetDate(moment(start).toDate());

//     const s = roundToQuarter(start);
//     const e = moment(s).add(15, "minutes").toDate();

//     availabilitySetterRef.current.open({
//       selectedDates: [s.toDate()],
//       startTime: s.toDate(),
//       endTime: e,
//       defaultPropertyId: propertyFilter !== "all" ? propertyFilter : "",
//     });
//   };

//   // ---------- Bulk clear (sequential per property to avoid Firebase overwrite races) ----------
//   const deleteIds = async (ids) => {
//     const list = (Array.isArray(ids) ? ids : [...ids]).map((x) => String(x));
//     if (!list.length) return;

//     // Group by property to keep sequential within each property (avoids set() race)
//     const byProperty = new Map();
//     for (const id of list) {
//       const a = availabilities.find((x) => String(x.id) === id);
//       if (!a) continue;
//       if (!byProperty.has(a.propertyId)) byProperty.set(a.propertyId, []);
//       byProperty.get(a.propertyId).push(id);
//     }

//     for (const [propertyId, idList] of byProperty.entries()) {
//       for (const id of idList) {
//         await deleteAvailability(propertyId, id); // sequential
//       }
//     }

//     setAvailabilities((prev) => prev.filter((a) => !list.includes(String(a.id))));
//     clearSelection();
//   };

//   const deleteSelected = () => {
//     const list = [...selectedIds]; // already strings
//     if (!list.length) return;
//     if (confirm(`Delete ${list.length} selected slot(s)?`)) {
//       deleteIds(list);
//     }
//   };

//   const effectiveDay = targetDate || currentDate;

//   const clearDay = () => {
//     const start = moment(effectiveDay).startOf("day");
//     const end = moment(effectiveDay).endOf("day");
//     const ids = events
//       .filter((e) => !e.__pending)
//       .filter((e) => moment(e.start).isSameOrAfter(start) && moment(e.end).isSameOrBefore(end))
//       .map((e) => String(e.id));
//     if (!ids.length) return;
//     if (confirm(`Delete ${ids.length} slot(s) on ${start.format("LL")}?`)) {
//       deleteIds(ids);
//     }
//   };

//   const clearWeek = () => {
//     const start = moment(effectiveDay).startOf("week");
//     const end = moment(effectiveDay).endOf("week");
//     const ids = events
//       .filter((e) => !e.__pending)
//       .filter((e) => moment(e.start).isSameOrAfter(start) && moment(e.end).isSameOrBefore(end))
//       .map((e) => String(e.id));
//     if (!ids.length) return;
//     if (confirm(`Delete ${ids.length} slot(s) for ${start.format("ll")} – ${end.format("ll")}?`)) {
//       deleteIds(ids);
//     }
//   };

//   const clearAllFiltered = () => {
//     const ids = events.filter((e) => !e.__pending).map((e) => String(e.id));
//     if (!ids.length) return;
//     if (confirm(`Delete ALL ${ids.length} slot(s) currently shown?`)) {
//       deleteIds(ids);
//     }
//   };

//   // Keyboard delete
//   useEffect(() => {
//     const onKey = (e) => {
//       if (e.key === "Delete" || e.key === "Backspace") {
//         if (selectedIds.size) {
//           e.preventDefault();
//           deleteSelected();
//         }
//       }
//     };
//     window.addEventListener("keydown", onKey);
//     return () => window.removeEventListener("keydown", onKey);
//   }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

//   // helper passed into event wrapper
//   const handleDragState = (phase, id) => {
//     if (phase === "start") {
//       draggingRef.current = true;
//       setDraggingId(id);
//     } else {
//       setTimeout(() => { draggingRef.current = false; }, 0);
//       setDraggingId(null);
//     }
//   };

//   /* ---------- render ---------- */
//   return (
//     <Box sx={CalendarScreenStyles.container}>
//       {/* back to dashboard */}
//       <IconButton
//         onClick={() => navigate(-1)}          /* or navigate("/dashboard") */
//         sx={{ position: "absolute", top: 16, left: 16 }}
//         aria-label="Back"
//       >
//         <ArrowBackIosNewIcon />
//       </IconButton>

//       <Typography variant="h4" sx={CalendarScreenStyles.title} mt={1}>
//         Viewing &amp; Maintenance Schedule
//       </Typography>

//       {/* property selector + bulk actions */}
//       <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
//         <FormControl size="small" sx={{ minWidth: 240 }}>
//           <InputLabel>Show slots for…</InputLabel>
//           <Select
//             value={propertyFilter}
//             label="Show slots for…"
//             onChange={(e) => setPropertyFilter(e.target.value)}
//           >
//             <MenuItem value="all">All Properties</MenuItem>
//             {properties.map((p) => (
//               <MenuItem key={p.id} value={p.id}>
//                 {p.name || p.address || `Property ${p.id}`}
//               </MenuItem>
//             ))}
//           </Select>
//         </FormControl>

//         <Tooltip title="Single-click selects • Double-click opens details • Delete key removes selection">
//           <FormControlLabel
//             control={<Switch checked={selectMode} onChange={(e) => setSelectMode(e.target.checked)} />}
//             label="Select Mode"
//           />
//         </Tooltip>

//         <Button size="small" variant="outlined" onClick={deleteSelected} disabled={!selectedIds.size}>
//           Delete Selected ({selectedIds.size})
//         </Button>
//         <Button size="small" variant="outlined" onClick={clearDay}>
//           Clear Day
//         </Button>
//         <Button size="small" variant="outlined" onClick={clearWeek}>
//           Clear Week
//         </Button>
//         <Button size="small" color="error" variant="outlined" onClick={clearAllFiltered}>
//           Clear All (Filtered)
//         </Button>

//         {/* Visual indicator of clicked day */}
//         {targetDate && (
//           <Chip
//             size="small"
//             label={`Target day: ${moment(targetDate).format("ll")}`}
//             sx={{ ml: 1 }}
//           />
//         )}

//         {!!pendingPreview.length && (
//           <Typography variant="caption">
//             Previewing {pendingPreview.length} unsaved slot(s)
//           </Typography>
//         )}
//       </Box>

//       <AvailabilitySetter
//         ref={availabilitySetterRef}
//         onAddAvailability={(newAv) => setAvailabilities((p) => [...p, ...newAv])}
//         onPreviewChange={setPendingPreview}
//         propertiesSeed={properties}
//       />

//       <Calendar
//         localizer={localizer}
//         events={events}
//         startAccessor="start"
//         endAccessor="end"
//         step={15}
//         timeslots={4}
//         selectable
//         min={moment().startOf("day").add(6, "h").toDate()}
//         max={moment().startOf("day").add(22, "h").toDate()}
//         defaultView={view}
//         defaultDate={currentDate}
//         onView={(v) => {
//           setView(v);
//           setTargetDate((d) => d);
//         }}
//         onNavigate={(date) => setCurrentDate(date)}
//         onSelecting={() => !selectMode}
//         onSelectSlot={(slot) => {
//           setTargetDate(slot.start);
//           if (selectMode) return;
//           handleSlotSelect(slot);
//         }}
//         // IMPORTANT: we no longer use RBC's onSelectEvent to avoid accidental popups
//         onDoubleClickEvent={handleDoubleClickEvent}
//         style={CalendarScreenStyles.calendar}
//         components={{
//           eventWrapper: (props) => (
//             <DraggableEventWrapper
//               {...props}
//               onDragState={handleDragState}
//               onSelect={handleEventClickSelect}
//             />
//           ),
//         }}
//         eventPropGetter={(e) => {
//           const base = e.isMaintenance ? "orange" : "green";
//           const isSelected = selectedIds.has(String(e.id));
//           const isDragging = draggingId === String(e.id);
//           const style = {
//             backgroundColor: base,
//             opacity: e.__pending ? 0.5 : (isDragging ? 0.75 : 1),
//             border: e.__pending
//               ? "2px dashed #555"
//               : (isSelected ? "2px solid #000" : (isDragging ? "2px solid #81c241" : "1px solid rgba(0,0,0,0.15)")),
//             boxShadow: isSelected
//               ? "0 0 0 3px rgba(0,0,0,0.15) inset"
//               : (isDragging ? "0 4px 12px rgba(0,0,0,0.24)" : undefined),
//             transform: isDragging ? "scale(0.98)" : undefined,
//             cursor: (!e.__pending && e.id) ? (isDragging ? "grabbing" : "grab") : "pointer",
//             transition: "transform 80ms ease, opacity 80ms ease, border 80ms ease",
//           };
//           return { style };
//         }}
//         dayPropGetter={(date) => {
//           const isTarget = targetDate && moment(date).isSame(targetDate, "day");
//           if (!isTarget) return {};
//           return {
//             style: {
//               backgroundColor: "rgba(25,118,210,0.08)",
//               outline: "2px solid #1976d2",
//               outlineOffset: "-2px",
//             },
//           };
//         }}
//         longPressThreshold={280} // friendlier touch behavior
//       />

//       {/* Drag-to-delete "Trash" target */}
//       <Box
//         onDragOver={(e) => {
//           e.preventDefault();
//           try { e.dataTransfer.dropEffect = "move"; } catch {}
//           setTrashHot(true);
//         }}
//         onDragEnter={() => setTrashHot(true)}
//         onDragLeave={() => setTrashHot(false)}
//         onDrop={(e) => {
//           setTrashHot(false);
//           try {
//             const idStr =
//               e.dataTransfer.getData("application/x-calendar-event-id") ||
//               e.dataTransfer.getData("text/plain");
//             if (idStr && confirm("Delete this slot?")) {
//               deleteIds([String(idStr).trim()]);
//             }
//           } catch {
//             /* noop */
//           }
//         }}
//         sx={{
//           position: "fixed",
//           bottom: 24,
//           right: 24,
//           width: 72,
//           height: 72,
//           borderRadius: "50%",
//           bgcolor: trashHot ? "#ef5350" : "#ef9a9a",
//           color: "#b71c1c",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           fontWeight: 700,
//           userSelect: "none",
//           boxShadow: 6,
//           zIndex: 2000, // ensure above calendar
//           transition: "background-color 120ms ease",
//           border: trashHot ? "3px solid #b71c1c" : "3px solid transparent",
//           cursor: "copy",
//         }}
//         title="Drag a slot here to delete"
//       >
//         🗑️
//       </Box>

//       {/* modal */}
//       <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
//         <Box sx={CalendarScreenStyles.modalContent}>
//           <Typography variant="h6">Event Details</Typography>
//           <Typography>Title: {selectedEvent?.title}</Typography>
//           <Typography>
//             Start:&nbsp;
//             {selectedEvent?.start && moment(selectedEvent.start).format("LLL")}
//           </Typography>
//           <Typography>
//             End:&nbsp;
//             {selectedEvent?.end && moment(selectedEvent.end).format("LLL")}
//           </Typography>

//           <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
//             <Button variant="outlined" onClick={handleDelete}>
//               Delete This
//             </Button>
//             <Button variant="contained" color="secondary" onClick={() => setModalOpen(false)}>
//               Close
//             </Button>
//           </Box>
//         </Box>
//       </Modal>
//     </Box>
//   );
// };

// export default CalendarScreen;



